/**
 * Shared period date helpers for Financial Close.
 */

import { MONTH_SHORT_NAMES } from './periodConstants.js';

/**
 * @param {string|Date|null|undefined} value
 * @returns {Date|null}
 */
export function toDate(value) {
  if (!value) return null;
  const date = value instanceof Date ? new Date(value) : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date;
}

/**
 * Format period range for display / search.
 * Same month & year → "Mar 1 – 31, 2026"
 * Different months, same year → "Mar 1 – Apr 30, 2026"
 * Different years → "Dec 1, 2025 – Jan 31, 2026"
 *
 * @param {string|Date} from
 * @param {string|Date} to
 * @returns {string}
 */
export function formatPeriodRange(from, to) {
  const start = toDate(from);
  const end = toDate(to);
  if (!start || !end) return '';

  const startDay = start.getUTCDate();
  const endDay = end.getUTCDate();
  const startMonth = MONTH_SHORT_NAMES[start.getUTCMonth()];
  const endMonth = MONTH_SHORT_NAMES[end.getUTCMonth()];
  const startYear = start.getUTCFullYear();
  const endYear = end.getUTCFullYear();

  if (startYear === endYear && start.getUTCMonth() === end.getUTCMonth()) {
    return `${startMonth} ${startDay} – ${endDay}, ${startYear}`;
  }

  if (startYear === endYear) {
    return `${startMonth} ${startDay} – ${endMonth} ${endDay}, ${startYear}`;
  }

  return `${startMonth} ${startDay}, ${startYear} – ${endMonth} ${endDay}, ${endYear}`;
}

/**
 * @param {Date} date
 * @returns {number|null} YYYYMM integer for range comparisons
 */
export function toYearMonthNumber(date) {
  if (!date) return null;
  return date.getUTCFullYear() * 100 + (date.getUTCMonth() + 1);
}

/**
 * Month tokens (`YYYY-MM`) from `from` through `to`, inclusive.
 *
 * @param {string|Date} from
 * @param {string|Date} to
 * @returns {string[]}
 */
export function enumerateMonthTokensBetween(from, to) {
  const start = toDate(from);
  const end = toDate(to);
  if (!start || !end) return [];

  const tokens = [];
  let year = start.getUTCFullYear();
  let month = start.getUTCMonth();
  const endYear = end.getUTCFullYear();
  const endMonth = end.getUTCMonth();

  while (year < endYear || (year === endYear && month <= endMonth)) {
    tokens.push(`${year}-${String(month + 1).padStart(2, '0')}`);
    month += 1;
    if (month > 11) {
      month = 0;
      year += 1;
    }
  }
  return tokens;
}

/**
 * Whether a month token overlaps a period range (inclusive).
 *
 * @param {string} token - `YYYY-MM`
 * @param {string|Date} from
 * @param {string|Date} to
 * @returns {boolean}
 */
export function monthTokenOverlapsPeriod(token, from, to) {
  if (!/^\d{4}-\d{2}$/.test(token)) return false;

  const [year, month] = token.split('-').map(Number);
  if (month < 1 || month > 12) return false;
  const tokenYM = year * 100 + month;
  const start = toDate(from);
  const end = toDate(to);
  const periodStart = start || end;
  const periodEnd = end || start;
  if (!periodStart || !periodEnd) return false;

  const startYM = toYearMonthNumber(periodStart);
  const endYM = toYearMonthNumber(periodEnd);
  const minYM = Math.min(startYM, endYM);
  const maxYM = Math.max(startYM, endYM);
  return tokenYM >= minYM && tokenYM <= maxYM;
}

/**
 * True when selection is the default "current year, all periods":
 * a year token (`YYYY`) or all 12 month tokens for the current calendar year.
 *
 * @param {string[]} periods
 * @returns {boolean}
 */
export function isCurrentYearAllPeriodSelection(periods) {
  const tokens = [...(periods || [])].map(String).sort();
  const year = String(new Date().getFullYear());
  if (tokens.length === 1 && tokens[0] === year) return true;
  if (tokens.length !== 12) return false;
  for (let month = 1; month <= 12; month += 1) {
    if (tokens[month - 1] !== `${year}-${String(month).padStart(2, '0')}`) {
      return false;
    }
  }
  return true;
}

/**
 * True when the period dropdown is applying a custom filter.
 * Empty (`All`) and current-year All are the baseline, not an active filter.
 *
 * @param {string[]} periods
 * @returns {boolean}
 */
export function isCustomPeriodFilter(periods) {
  const tokens = periods || [];
  if (!tokens.length) return false;
  return !isCurrentYearAllPeriodSelection(tokens);
}

/**
 * Whether a calendar year overlaps a period range (inclusive).
 *
 * @param {number} year
 * @param {string|Date} from
 * @param {string|Date} to
 * @returns {boolean}
 */
export function yearOverlapsPeriod(year, from, to) {
  const start = toDate(from);
  const end = toDate(to);
  const periodStart = start || end;
  const periodEnd = end || start;
  if (!periodStart || !periodEnd) return false;

  const minYear = Math.min(
    periodStart.getUTCFullYear(),
    periodEnd.getUTCFullYear()
  );
  const maxYear = Math.max(
    periodStart.getUTCFullYear(),
    periodEnd.getUTCFullYear()
  );
  return year >= minYear && year <= maxYear;
}
